// ADR-109 — apps/remote_assist frontend store
// حالت UI محلی سشن جاری (کانکشن WebSocket، نقش کاربر، وضعیت اتصال) — جدا
// از React Query که خودِ داده‌ی سرور (RemoteAssistSession) را نگه می‌دارد.
//
// AM-048: علاوه بر AM-045، بک‌اند حالا کدهای close مجزا می‌فرستد و store دلیل
// بسته‌شدن را (closedReason) نگه می‌دارد تا بنر F2 مستقل از polling سشن‌ها
// نمایش داده شود. disconnect() دستی عمداً closedReason را پاک نمی‌کند؛
// فقط connect() جدید یا dismissClosedReason() آن را پاک می‌کند.

import { create } from "zustand";
import type { RemoteAssistChannelMessage } from "../types";

export type RemoteAssistRole = "employee" | "supporter" | "vendor_supporter";
export type PeerConnectionStatus = "connected" | "peer_disconnected" | "peer_reconnected";

// AM-048: باید دقیقاً با apps/remote_assist/close_codes.py هم‌خوان بماند.
export const CLOSE_FORBIDDEN = 4003;
export const CLOSE_NOT_FOUND = 4004;
export const CLOSE_SESSION_ENDED = 4005;
export const CLOSE_ACCESS_REVOKED = 4006;
export const CLOSE_CONNECTION_LOST = 4007;

export type ClosedReason = "forbidden" | "ended" | "revoked" | "connection_lost";

// همه‌ی این کدها «رد/پایان صریح بک‌اند»اند — تلاش دوباره فایده‌ای ندارد.
const CLOSE_CODE_REASON: Record<number, ClosedReason> = {
  [CLOSE_FORBIDDEN]: "forbidden",
  [CLOSE_NOT_FOUND]: "forbidden",
  [CLOSE_SESSION_ENDED]: "ended",
  [CLOSE_ACCESS_REVOKED]: "revoked",
  [CLOSE_CONNECTION_LOST]: "connection_lost",
};

interface RemoteAssistChannelState {
  sessionId: string | null;
  role: RemoteAssistRole | null;
  socket: WebSocket | null;
  connectionStatus: "idle" | "connecting" | "open" | "closed" | "error";
  lastError: string | null;
  peerStatus: PeerConnectionStatus;
  lastPeerActivityAt: number | null;
  // AM-048
  closedReason: ClosedReason | null;
  closedRole: RemoteAssistRole | null;

  connect: (params: {
    sessionId: string;
    role: RemoteAssistRole;
    url: string;
    onMessage: (message: RemoteAssistChannelMessage) => void;
  }) => void;
  send: (message: RemoteAssistChannelMessage) => void;
  disconnect: () => void;
  dismissClosedReason: () => void;
}

let pingIntervalId: ReturnType<typeof setInterval> | null = null;
let staleWatchdogId: ReturnType<typeof setInterval> | null = null;
let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

const PING_INTERVAL_MS = 10_000;
const STALE_THRESHOLD_MS = 25_000;

// AM-045 F1 fix: تا این پچ، وقتی سوکت بی‌صدا قطع می‌شد (خواب لپ‌تاپ، افت شبکه،
// ری‌استارت پروکسی) هیچ تلاشی برای اتصال دوباره وجود نداشت — مهلت ۵ تا ۱۰
// دقیقه‌ای بک‌اند (AM-045) عملاً فقط با رفرش دستی صفحه قابل‌استفاده بود.
// backoff نمایی با سقف ۱۰ ثانیه، تا وقتی بک‌اند با یکی از کدهای CLOSE_CODE_REASON
// صراحتاً رد/پایان ندهد یا disconnect() دستی صدا زده نشود.
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 10_000;

let reconnectAttempt = 0;
let manualDisconnect = true; // تا اولین connect() صدا زده نشده، چیزی برای بازاتصال نیست
let lastConnectParams: {
  sessionId: string;
  role: RemoteAssistRole;
  url: string;
  onMessage: (message: RemoteAssistChannelMessage) => void;
} | null = null;

function clearAm045Timers() {
  if (pingIntervalId) clearInterval(pingIntervalId);
  if (staleWatchdogId) clearInterval(staleWatchdogId);
  if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
  pingIntervalId = null;
  staleWatchdogId = null;
  reconnectTimeoutId = null;
}

export const useRemoteAssistChannelStore = create<RemoteAssistChannelState>((set, get) => ({
  sessionId: null,
  role: null,
  socket: null,
  connectionStatus: "idle",
  lastError: null,
  peerStatus: "connected",
  lastPeerActivityAt: null,
  closedReason: null,
  closedRole: null,

  connect: ({ sessionId, role, url, onMessage }) => {
    const current = get();
    if (current.socket && current.sessionId === sessionId && current.connectionStatus === "open") {
      return;
    }
    manualDisconnect = false;
    lastConnectParams = { sessionId, role, url, onMessage };
    current.socket?.close();
    clearAm045Timers();

    set({
      sessionId,
      role,
      connectionStatus: "connecting",
      lastError: null,
      peerStatus: "connected",
      lastPeerActivityAt: Date.now(),
      closedReason: null, // AM-048: اتصال جدید = دلیل بسته‌شدنِ قبلی منقضی است
      closedRole: null,
    });

    const socket = new WebSocket(url);

    socket.onopen = () => {
      reconnectAttempt = 0; // AM-045 F1: اتصال موفق شد — شمارنده‌ی backoff صفر می‌شود
      set({ connectionStatus: "open", lastPeerActivityAt: Date.now() });

      pingIntervalId = setInterval(() => {
        get().send({ type: "ping" } as RemoteAssistChannelMessage);
      }, PING_INTERVAL_MS);

      staleWatchdogId = setInterval(() => {
        const { lastPeerActivityAt, peerStatus, send } = get();
        if (
          lastPeerActivityAt !== null &&
          Date.now() - lastPeerActivityAt > STALE_THRESHOLD_MS &&
          peerStatus === "connected"
        ) {
          send({ type: "report_peer_disconnected" } as RemoteAssistChannelMessage);
          set({ peerStatus: "peer_disconnected" });
        }
      }, 5_000);
    };

    socket.onclose = (event) => {
      clearAm045Timers();
      set({ connectionStatus: "closed" });

      // AM-048: کدهای ۴۰۰۳ تا ۴۰۰۷ یعنی بک‌اند صراحتاً رد کرده یا سشن را پایان
      // داده — دلیلش را نگه می‌داریم (فقط اگر خودمان دستی قطع نکرده باشیم) و
      // تلاش دوباره نمی‌کنیم. هر بستنِ دیگر (افت شبکه، ری‌استارت پروکسی) قابل‌بازیابی است.
      const reason = event?.code !== undefined ? CLOSE_CODE_REASON[event.code] : undefined;
      if (reason && !manualDisconnect) {
        set({ closedReason: reason, closedRole: get().role });
      }

      if (manualDisconnect || reason !== undefined || !lastConnectParams) {
        return;
      }
      reconnectAttempt += 1;
      const delayMs = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempt - 1),
        RECONNECT_MAX_DELAY_MS
      );
      reconnectTimeoutId = setTimeout(() => {
        if (manualDisconnect || !lastConnectParams) return;
        get().connect(lastConnectParams);
      }, delayMs);
    };

    socket.onerror = () => {
      set({ connectionStatus: "error", lastError: "خطا در اتصال به کانال Remote Assist" });
    };

    socket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as RemoteAssistChannelMessage;

        if (parsed.type === "dom_patch" || parsed.type === "input_event" || parsed.type === "ping") {
          set({ lastPeerActivityAt: Date.now() });
          if (get().peerStatus !== "connected") {
            set({ peerStatus: "connected" });
          }
        } else if (parsed.type === "peer_disconnected") {
          set({ peerStatus: "peer_disconnected" });
        } else if (parsed.type === "peer_reconnected") {
          set({ peerStatus: "peer_reconnected", lastPeerActivityAt: Date.now() });
          setTimeout(() => {
            if (get().peerStatus === "peer_reconnected") set({ peerStatus: "connected" });
          }, 4_000);
        } else if (parsed.type === "session_ended") {
          const rawReason = (parsed as unknown as { reason?: string }).reason;
          const mappedReason: ClosedReason =
            rawReason === "connection_lost" || rawReason === "revoked" || rawReason === "forbidden"
              ? rawReason
              : "ended";
          set({ closedReason: mappedReason, closedRole: get().role });
        }

        onMessage(parsed);
      } catch {
        // پیام نامعتبر — نادیده گرفته می‌شود، اتصال قطع نمی‌شود.
      }
    };

    set({ socket });
  },

  send: (message) => {
    const { socket, connectionStatus } = get();
    if (socket && connectionStatus === "open") {
      socket.send(JSON.stringify(message));
    }
  },

  // AM-048: closedReason/closedRole عمداً اینجا پاک نمی‌شوند — overlay هنگام
  // unmount/پایان سشن disconnect() صدا می‌زند و بنر F2 باید بعد از آن هم بماند.
  disconnect: () => {
    const { socket } = get();
    manualDisconnect = true;
    lastConnectParams = null;
    reconnectAttempt = 0;
    clearAm045Timers();
    socket?.close();
    set({
      sessionId: null,
      role: null,
      socket: null,
      connectionStatus: "idle",
      peerStatus: "connected",
      lastPeerActivityAt: null,
    });
  },

  dismissClosedReason: () => set({ closedReason: null, closedRole: null }),
}));
